import { useEffect, useState } from "react";

export const GITHUB_USERNAME = "brysonwaisi";
const API = "https://api.github.com";
const CACHE_KEY = `portfolio:github:profile:${GITHUB_USERNAME}`;
const CACHE_TTL = 60 * 1000;

function fallbackState(status = "idle") {
  return {
    status,
    fetchedAt: null,
    profile: {
      login: GITHUB_USERNAME,
      publicRepos: 111,
      followers: 22,
      following: null,
    },
    starredCount: 40,
  };
}

function readCache() {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (!cached.fetchedAt || Date.now() - cached.fetchedAt > CACHE_TTL) {
      return null;
    }
    return { ...cached, status: "cached" };
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // localStorage can be unavailable in private or restricted browser modes.
  }
}

async function fetchJson(path, signal) {
  const response = await fetch(`${API}${path}`, {
    headers: { Accept: "application/vnd.github+json" },
    cache: "no-store",
    signal,
  });
  if (!response.ok) {
    throw new Error(`GitHub API ${response.status}: ${path}`);
  }
  return response.json();
}

async function fetchCount(path, signal) {
  const response = await fetch(`${API}${path}`, {
    headers: { Accept: "application/vnd.github+json" },
    cache: "no-store",
    signal,
  });
  if (!response.ok) {
    throw new Error(`GitHub API ${response.status}: ${path}`);
  }

  const link = response.headers.get("Link") || "";
  const lastPage = link.match(/[?&]page=(\d+)>;\s*rel="last"/);
  if (lastPage) return Number(lastPage[1]);

  const data = await response.json();
  return Array.isArray(data) ? data.length : 0;
}

async function fetchGithubStats(signal) {
  const fallback = fallbackState("ready");

  const [profileResult, starredResult] = await Promise.allSettled([
    fetchJson(`/users/${GITHUB_USERNAME}`, signal),
    fetchCount(`/users/${GITHUB_USERNAME}/starred?per_page=1`, signal),
  ]);

  const profile =
    profileResult.status === "fulfilled"
      ? {
          login: profileResult.value.login,
          publicRepos: profileResult.value.public_repos,
          followers: profileResult.value.followers,
          following: profileResult.value.following,
        }
      : fallback.profile;

  return {
    status: "ready",
    fetchedAt: Date.now(),
    profile,
    starredCount:
      starredResult.status === "fulfilled"
        ? starredResult.value
        : fallback.starredCount,
  };
}

export default function useGithubStats() {
  const [github, setGithub] = useState(() => readCache() || fallbackState());

  useEffect(() => {
    const controller = new AbortController();

    fetchGithubStats(controller.signal)
      .then((data) => {
        writeCache(data);
        setGithub(data);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.warn("Unable to load GitHub stats", error);
        setGithub((current) => ({
          ...current,
          status: current.fetchedAt ? "cached" : "fallback",
        }));
      });

    return () => controller.abort();
  }, []);

  return github;
}
